import i18n from '@/i18n';

/**
 * Format a date as localized relative time (e.g., "há 2 minutos", "2 hours ago")
 * @param date - Date to format (string or Date object)
 * @returns Formatted relative time string
 */
export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '-';

  const now = new Date();
  const targetDate = typeof date === 'string' ? new Date(date) : date;

  // Check for invalid date
  if (isNaN(targetDate.getTime())) {
    return '-';
  }

  const diffInSeconds = Math.floor((targetDate.getTime() - now.getTime()) / 1000);
  const locale = i18n.resolvedLanguage || i18n.language || 'en';

  // Future date
  if (diffInSeconds > 0) {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(
      targetDate
    );
  }

  const relativeTime = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const elapsedSeconds = Math.abs(diffInSeconds);

  // Less than a minute
  if (elapsedSeconds < 60) {
    if (elapsedSeconds < 10) return i18n.t('common:time.justNow');
    return relativeTime.format(-elapsedSeconds, 'second');
  }

  // Less than an hour
  const diffInMinutes = Math.floor(elapsedSeconds / 60);
  if (diffInMinutes < 60) {
    return relativeTime.format(-diffInMinutes, 'minute');
  }

  // Less than a day
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return relativeTime.format(-diffInHours, 'hour');
  }

  // Less than a week
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return relativeTime.format(-diffInDays, 'day');
  }

  // Less than a month
  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return relativeTime.format(-diffInWeeks, 'week');
  }

  // Less than a year
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return relativeTime.format(-diffInMonths, 'month');
  }

  // More than a year
  const diffInYears = Math.floor(diffInDays / 365);
  return relativeTime.format(-diffInYears, 'year');
}

/**
 * Format a date with tooltip showing exact date/time
 * @param date - Date to format
 * @returns Object with relative time and full formatted date
 */
export function formatRelativeTimeWithTooltip(date: string | Date | null | undefined): {
  relative: string;
  tooltip: string;
} {
  if (!date) {
    return { relative: '-', tooltip: '-' };
  }

  const targetDate = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(targetDate.getTime())) {
    return { relative: '-', tooltip: '-' };
  }

  return {
    relative: formatRelativeTime(date),
    tooltip: new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language || 'en', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(targetDate),
  };
}
